import { type ApolloError, gql, useMutation } from '@apollo/client';
import { useState } from 'react';
import type { DealDetailsFormValues } from '@/admins/MaDeals/components/MaDealDetails/components/DealDetailsForm/types';
import type { MaDealDetails, MaDealDetailsInput } from '@/types/GraphqlSchema';

const UPDATE_DEAL_DETAILS_MUTATION = gql`
  mutation updateMaDealDetails($dealId: ID!, $input: MaDealDetailsInput!) {
    updateMaDealDetails(dealId: $dealId, input: $input) {
      id
      versions {
        id
        createdAt
        objectChanges
        user {
          id
          email
          firstName
          lastName
        }
      }
    }
  }
`;

type UseUpdateDealDetailsResult = {
  updateDealDetails: (dealId: number, dealDetails: DealDetailsFormValues) => void;
  data: MaDealDetails | undefined;
  loading: boolean;
  error: ApolloError | Error | undefined;
};

export const useUpdateDealDetails: () => UseUpdateDealDetailsResult = () => {
  const [errorState, setErrorState] = useState<ApolloError | Error | undefined>(undefined);
  const [updateDealDetailsMutation, { data, loading, error }] = useMutation(
    UPDATE_DEAL_DETAILS_MUTATION,
  );

  const updateDealDetails = (dealId: number, dealDetails: DealDetailsFormValues) => {
    try {
      const dealDetailsInput = {
        acreage: Number(dealDetails.acreage),
        dealStatus: dealDetails?.dealStatus,
        closingDate: dealDetails?.closingDate
          ? new Date(dealDetails.closingDate as string).toISOString()
          : null,
        announcementDate: dealDetails?.announcementDate
          ? new Date(dealDetails.announcementDate as string).toISOString()
          : null,
        buySideAdvisor: dealDetails?.buySideAdvisor,
        sellSideAdvisor: dealDetails?.sellSideAdvisor,
        sellerMotivation: dealDetails?.sellerMotivation,
        paymentType: dealDetails?.paymentType,
        reportedConsiderationComment: dealDetails?.reportedConsiderationComment,
        reportedConsiderationBaseCashMmusd: Number(dealDetails.reportedConsiderationBaseCashMmusd),
        reportedConsiderationBaseEquityMmusd: Number(
          dealDetails.reportedConsiderationBaseEquityMmusd,
        ),
        reportedConsiderationContingentMmusd: Number(
          dealDetails.reportedConsiderationContingentMmusd,
        ),
        reportedDebtMmusd: Number(dealDetails.reportedDebtMmusd),
        reportedCostCarryMmusd: Number(dealDetails.reportedCostCarryMmusd),
        reportedConsiderationTotalMmusd: Number(dealDetails.reportedConsiderationTotalMmusd),
        reportedSignatureBonusMmusd: Number(dealDetails.reportedSignatureBonusMmusd),
        reportedCostCommitmentMmusd: Number(dealDetails.reportedCostCommitmentMmusd),
        reportedTransacted1pReservesMmboe: Number(dealDetails.reportedTransacted1pReservesMmboe),
        reportedTransacted1pOilCondensateReservesMmbbl: Number(
          dealDetails.reportedTransacted1pOilCondensateReservesMmbbl,
        ),
        reportedTransacted1pGasReservesBcf: Number(dealDetails.reportedTransacted1pGasReservesBcf),
        reportedTransacted2pReservesMmboe: Number(dealDetails.reportedTransacted2pReservesMmboe),
        reportedTransacted2pOilCondensateReservesMmbbl: Number(
          dealDetails.reportedTransacted2pOilCondensateReservesMmbbl,
        ),
        reportedTransacted2pGasReservesBcf: Number(dealDetails.reportedTransacted2pGasReservesBcf),
        reportedTransacted2cResourceMmboe: Number(dealDetails.reportedTransacted2cResourceMmboe),
        reportedTransacted2cOilCondensateResourceMmbbl: Number(
          dealDetails.reportedTransacted2cOilCondensateResourceMmbbl,
        ),
        reportedTransacted2cGasResourceBcf: Number(dealDetails.reportedTransacted2cGasResourceBcf),
        reportedProductionBoed: Number(dealDetails.reportedProductionBoed),
        reportedOilCondensateProductionBbld: Number(
          dealDetails.reportedOilCondensateProductionBbld,
        ),
        reportedGasProductionMmcfd: Number(dealDetails.reportedGasProductionMmcfd),
        reportedReservesAcquisitionCostUsdPerBoe: Number(
          dealDetails.reportedReservesAcquisitionCostUsdPerBoe,
        ),
        reportedAcreageAcquisitionCostUsdPerAcre: Number(
          dealDetails.reportedAcreageAcquisitionCostUsdPerAcre,
        ),
        reportedPricePerFlowingBarrelUsdPerBoed: Number(
          dealDetails.reportedPricePerFlowingBarrelUsdPerBoed,
        ),
        reportedCurrentEmissionsIntensityKgco2ePerBoe: Number(
          dealDetails.reportedCurrentEmissionsIntensityKgco2ePerBoe,
        ),
        reportedRemainingEmissionsIntensityKgco2ePerBoe: Number(
          dealDetails.reportedRemainingEmissionsIntensityKgco2ePerBoe,
        ),
      } as MaDealDetailsInput;

      updateDealDetailsMutation({ variables: { dealId, input: dealDetailsInput } });
    } catch (e) {
      setErrorState(e as Error);
    }
  };

  return {
    updateDealDetails,
    data: data?.updateMaDealDetails,
    loading,
    error: error || errorState,
  };
};
